import { useGetFullVersionInfoQuery } from '@/api/versionApi';
import useTimezone from '@/hooks/useTimezone';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Modal, Space, Spin, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';

const { Text } = Typography;

const DATE_FORMAT = 'MM/DD/YYYY hh:mm A';

const orDash = (value) =>
  value === null || value === undefined || value === '' ? <Text type="secondary">-</Text> : value;

// Shared by the frontend (__BUILD_INFO__, set in vite.config.js) and the backend
// (/version/fullVersionInfo), which use the same field names.
const buildInfoItems = (info, formatDateTime) => [
  { key: 'branch', label: 'Branch', children: orDash(info.branch) },
  {
    key: 'commit',
    label: 'Commit',
    children: info.commitIdAbbrev ? (
      <Space>
        <Text copyable code>
          {info.commitIdAbbrev}
        </Text>
        {info.dirty && <Tag color="orange">dirty</Tag>}
      </Space>
    ) : (
      orDash(null)
    ),
  },
  {
    key: 'commitMessage',
    label: 'Commit Message',
    children: orDash(info.commitMessageShort),
  },
  { key: 'commitUser', label: 'Commit Author', children: orDash(info.commitUserName) },
  {
    key: 'commitTime',
    label: 'Commit Time',
    children: formatDateTime(info.commitTime, DATE_FORMAT),
  },
  { key: 'buildHost', label: 'Build Host', children: orDash(info.buildHost) },
  { key: 'buildUser', label: 'Built By', children: orDash(info.buildUserName) },
];

// Values baked in at build time from the .env file
const configItems = [
  { key: 'env', label: 'Environment', children: orDash(import.meta.env.VITE_ENV) },
  {
    key: 'backendHost',
    label: 'Backend Host',
    children: orDash(import.meta.env.VITE_BACKEND_HOST),
  },
  {
    key: 'keycloakAuthority',
    label: 'Keycloak Authority',
    children: orDash(import.meta.env.VITE_KEYCLOAK_AUTHORITY),
  },
];

const descriptionProps = {
  bordered: true,
  size: 'small',
  column: 1,
  styles: { label: { width: 180 } },
};

const VersionInfoModal = ({ open, onCancel }) => {
  const { formatDateTime } = useTimezone();
  const { data, isLoading, isFetching, isError } = useGetFullVersionInfoQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined />
          Version Info
        </Space>
      }
      open={open}
      onCancel={onCancel}
      onOk={onCancel}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={700}
      destroyOnHidden
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Descriptions
          title="Frontend"
          items={[...buildInfoItems(__BUILD_INFO__, formatDateTime), ...configItems]}
          {...descriptionProps}
        />

        <Spin spinning={isLoading || isFetching}>
          {isError ? (
            <Alert type="error" showIcon message="Unable to load backend version info." />
          ) : (
            <Descriptions
              title="Backend"
              items={data ? buildInfoItems(data, formatDateTime) : []}
              {...descriptionProps}
            />
          )}
        </Spin>
      </Space>
    </Modal>
  );
};

VersionInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default VersionInfoModal;
